import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { notification, Radio, Alert, Spin } from 'antd';
import { isAxiosError } from 'axios';
import styles from './organizational-structure.module.scss';
import { getLang } from 'shared/store/selectors/lang.selector';
import { Select } from 'src/components/design-system';
import { Modal } from 'src/components/design-system/modal';
import { formatNumber, getApiErrorMessage } from 'shared/utils';
import {
  useDeleteProfileFieldOption,
  useProfileFieldOptionAffectedCount,
} from 'shared/hooks/profile-fields.hooks';
import { INTERNAL_ROUTING, ROUTES } from 'shared/constants/routes';
import {
  AFFECTED_USERS_SCOPES,
  PROFILE_FIELDS_ERROR_CODES,
} from 'shared/constants/profile-fields.constants';
import { EVENTS } from 'shared/constants/analytics';
import analyticsService from 'src/helpers/analytics.service';
import { fetchUsers, fetchUserStats } from 'shared/store/actions/users-management.actions';
import { fetchAuthenticatedUser } from 'shared/store/actions/auth.actions';

/** Poll the affected-user counts while the modal is open; they change as admins fix profiles. */
const AFFECTED_USERS_POLL_MS = 10_000;

/**
 * Delete-option modal. States how many users have the option, then offers
 * remove vs. replace. Remove is disabled — forcing a replacement — when the
 * field is mandatory, it's the last option, or other options depend on it
 * (mirrors the backend guards). Destructive: red confirm button.
 *
 * The user count links to User Management filtered to those users. An option that
 * still has child options (a profession with roles, a role with levels) is blocked
 * while any user has it: the modal shows only that count (linked the same way) and
 * delete stays disabled, so the admin moves those users first.
 *
 * @param {{
 *   open: boolean,
 *   field: import('shared/api/profile-fields.service.api').ProfileField,
 *   option: import('shared/api/profile-fields.service.api').ProfileFieldOption | null,
 *   optionIdsWithChildren: Set<string>,
 *   childFieldLabel: string | null,
 *   parentFieldLabel: string | null,
 *   stats: Record<string, Record<string, number>> | undefined,
 *   onClose: () => void,
 * }} props
 */
export function DeleteOptionModal({
  open,
  field,
  option,
  optionIdsWithChildren,
  childFieldLabel,
  parentFieldLabel,
  stats,
  onClose,
}) {
  const lang = useSelector(getLang('SETTINGS'));
  const [api, contextHolder] = notification.useNotification();
  const deleteOption = useDeleteProfileFieldOption();
  const dispatch = useDispatch();

  // Scope replacements to the same parent as the deleted option, so a dependent
  // option (role / levelOfTraining) can't be reassigned across parents. Non-
  // dependent options all have parentOptionId null, so this is a no-op for them.
  const parentScope = option?.parentOptionId ?? null;
  const replacementOptions = useMemo(
    () =>
      (field.options || [])
        .filter(o => o._id !== option?._id && (o.parentOptionId ?? null) === parentScope)
        .map(o => ({ value: o._id, label: o.label || o.code })),
    [field.options, option, parentScope],
  );

  const hasChildren = !!option && optionIdsWithChildren.has(option._id);
  const isLastOption = (field.options || []).length <= 1;
  // A role (or level) is required while its profession (or role) has any, so removing
  // one that still has siblings would leave users with an invalid profile: Replace only.
  // The last one under its parent has nothing to replace it with, and once it's gone
  // nothing requires it, so Remove stays allowed there (mirrors the backend's guard).
  const replaceOnly = !!option?.parentOptionId && replacementOptions.length > 0;
  const removeDisabled = field.isMandatory || isLastOption || hasChildren || replaceOnly;

  const [shouldReplace, setShouldReplace] = useState(removeDisabled);
  const [replacementOptionId, setReplacementOptionId] = useState('');

  useEffect(() => {
    if (open) {
      setShouldReplace(removeDisabled);
      setReplacementOptionId('');
    }
  }, [open, option?._id, removeDisabled]);

  const fieldLabel = (field.displayLabel || field.fieldKey || '').toLowerCase();
  const optionLabel = option ? option.label || option.code : '';
  const affectedCount = option ? (stats?.[field.fieldKey]?.[option.code] ?? 0) : 0;

  const getRemoveDisabledReason = () => {
    if (field.isMandatory) return lang.DELETE_REMOVE_DISABLED_MANDATORY;
    if (isLastOption) return lang.DELETE_REMOVE_DISABLED_LAST;
    if (hasChildren) return lang.DELETE_REMOVE_DISABLED_CHILDREN;
    if (replaceOnly) {
      return lang.DELETE_REMOVE_DISABLED_PARENT.replace('{field}', fieldLabel).replace(
        '{parentField}',
        (parentFieldLabel || '').toLowerCase(),
      );
    }
    return null;
  };
  const removeDisabledReason = getRemoveDisabledReason();

  // Count only — the email list is loaded by User Management when the link is followed.
  const holderUsersQuery = useProfileFieldOptionAffectedCount(
    { fieldId: field.id, optionId: option?._id, scope: AFFECTED_USERS_SCOPES.HOLDERS },
    { enabled: open && !!option, refetchInterval: AFFECTED_USERS_POLL_MS },
  );
  // Prefer the HOLDERS count over the Redux stats, which also count hidden and
  // deleted subscriptions, so the dialog never shows two different numbers.
  const usedCount = holderUsersQuery.data ?? affectedCount;

  // A parent (a profession with roles, a role with levels) is blocked while any user
  // has it: deleting it clears their children. Fixing those profiles in User Management
  // unblocks it; with nobody on it, its child options go with it. Nothing can be chosen
  // yet, so the modal shows only whom to fix. Fail closed while the count is unknown
  // (first load or failed); background polls keep the last count, so the button doesn't
  // flicker, and a change between polls is caught by the backend's 409.
  const isBlocked = hasChildren && (holderUsersQuery.data ?? 0) > 0;
  const blockedWhileUnknown =
    hasChildren && (holderUsersQuery.isPending || holderUsersQuery.isError);
  // The layout depends on the first count: show a loader until it lands rather than the
  // remove/replace form for a second and then the block.
  const isLoadingCount = holderUsersQuery.isPending;

  // Moving users onto a replacement that has child options would leave them without a
  // required child value (a profession with roles needs a role), so that choice blocks.
  const replacementHasChildren =
    shouldReplace && !!replacementOptionId && optionIdsWithChildren.has(replacementOptionId);
  const blockedByReplacement = replacementHasChildren && usedCount > 0;
  const replacementLabel =
    replacementOptions.find(o => o.value === replacementOptionId)?.label ?? '';

  const submitDisabled =
    isLoadingCount ||
    isBlocked ||
    blockedWhileUnknown ||
    blockedByReplacement ||
    (shouldReplace && !replacementOptionId);

  // `template` holds a `{users}` placeholder, rendered as a link to User Management,
  // which loads the users of `scope` from the ids in the URL and filters to them.
  // With no users there is nothing to open, so the count is plain text.
  const renderUsersLinkMessage = (template, scope, total) => {
    const [before, after] = template
      .replaceAll('{field}', fieldLabel)
      .replaceAll('{childField}', (childFieldLabel || '').toLowerCase())
      .split('{users}');
    const linkText = (
      total === 1 ? lang.DELETE_AFFECTED_USERS_LINK_ONE : lang.DELETE_AFFECTED_USERS_LINK
    ).replace('{count}', formatNumber(total));
    const path = `${ROUTES.USERS_MANAGEMENT}?${new URLSearchParams({
      [INTERNAL_ROUTING.QUERY_PARAMS.USERS_AFFECTED_FIELD]: field.id,
      [INTERNAL_ROUTING.QUERY_PARAMS.USERS_AFFECTED_OPTION]: option?._id ?? '',
      [INTERNAL_ROUTING.QUERY_PARAMS.USERS_AFFECTED_SCOPE]: scope,
    })}`;
    return (
      <>
        {before}
        {total > 0 ? (
          <Link to={path} className={styles.usersLink}>
            {linkText}
          </Link>
        ) : (
          linkText
        )}
        {after}
      </>
    );
  };

  // "N users have this {field}." — hidden when nobody has it; `suffix` follows the sentence.
  const renderUsedCountLine = (suffix = '') =>
    usedCount > 0 && (
      <p className={styles.modalBody}>
        {renderUsersLinkMessage(
          usedCount === 1 ? lang.DELETE_USERS_HAVE_OPTION_ONE : lang.DELETE_USERS_HAVE_OPTION,
          AFFECTED_USERS_SCOPES.HOLDERS,
          usedCount,
        )}
        {suffix}
      </p>
    );

  const handleDelete = async () => {
    if (!option) return;
    try {
      await deleteOption.mutateAsync({
        fieldId: field.id,
        optionId: option._id,
        shouldReplace,
        replacementOptionId: shouldReplace ? replacementOptionId : undefined,
      });
      analyticsService.track(EVENTS.PROFILE_FIELDS.DELETE_OPTION, {
        group: field.group,
        fieldKey: field.fieldKey,
        optionId: option._id,
        code: option.code,
        label: option.label,
        replacementOptionId: shouldReplace ? replacementOptionId : null,
        affectedCount,
        holderUsersCount: holderUsersQuery.data ?? null,
      });
      // The reassign rewrites subscription field values, so the Redux user
      // list + stats (cache-first, won't self-refresh) are now stale — refetch.
      dispatch(fetchUsers());
      dispatch(fetchUserStats());
      dispatch(fetchAuthenticatedUser()).catch(() => {});
      api.success({ message: lang.DELETE_SUCCESS, placement: 'bottomRight' });
      onClose();
    } catch (error) {
      const hasAffectedUsers =
        isAxiosError(error) &&
        error.response?.data?.code === PROFILE_FIELDS_ERROR_CODES.OPTION_HAS_AFFECTED_USERS;
      api.error({
        message: hasAffectedUsers
          ? lang.DELETE_BLOCKED_BY_AFFECTED_USERS.replace('{field}', fieldLabel)
          : getApiErrorMessage(error) || lang.ORG_STRUCTURE_ERROR,
        placement: 'bottomRight',
      });
      // 409: a user got the option while the dialog was open — show the fresh count.
      if (isAxiosError(error) && error.response?.status === 409) {
        holderUsersQuery.refetch();
      }
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={lang.DELETE_ITEM_TITLE.replace('{name}', optionLabel)}
        open={open}
        onCancel={onClose}
        onSubmit={handleDelete}
        cancelText={lang.CANCEL}
        submitText={lang.DELETE_CONFIRM}
        submitDisabled={submitDisabled}
        submitDanger
        loading={deleteOption.isPending}
        footerAlign="end"
        className={styles.modal}>
        {isLoadingCount ? (
          <div className={styles.countLoading}>
            <Spin />
          </div>
        ) : isBlocked ? (
          <Alert
            type="error"
            showIcon
            message={renderUsersLinkMessage(
              lang.DELETE_BLOCKED_BY_USERS,
              AFFECTED_USERS_SCOPES.HOLDERS,
              usedCount,
            )}
            className={styles.disclaimer}
          />
        ) : (
          <>
            {renderUsedCountLine(` ${lang.DELETE_WHAT_SHOULD_HAPPEN}`)}

            {removeDisabledReason && (
              <Alert
                type="info"
                showIcon
                message={removeDisabledReason}
                className={styles.disclaimer}
              />
            )}

            <Radio.Group
              className={styles.radioGroup}
              value={shouldReplace}
              onChange={e => setShouldReplace(e.target.value)}>
              <Radio value={false} disabled={removeDisabled}>
                {lang.DELETE_REMOVE_VALUE}
              </Radio>
              <Radio value={true}>{lang.DELETE_REPLACE_WITH.replace('{field}', fieldLabel)}</Radio>
            </Radio.Group>

            {shouldReplace && (
              <div className={styles.replaceSelect}>
                <Select
                  name="replacementOptionId"
                  value={replacementOptionId}
                  onChange={(_, value) => setReplacementOptionId(value)}
                  options={replacementOptions}
                  placeholder={lang.DELETE_SELECT_REPLACEMENT.replace(
                    '{field}',
                    field.displayLabel,
                  )}
                  size="middle"
                />
              </div>
            )}

            {blockedByReplacement && (
              <Alert
                type="error"
                showIcon
                message={renderUsersLinkMessage(
                  lang.DELETE_REPLACEMENT_HAS_CHILDREN.replaceAll(
                    '{replacement}',
                    replacementLabel,
                  ),
                  AFFECTED_USERS_SCOPES.HOLDERS,
                  usedCount,
                )}
                className={styles.disclaimer}
              />
            )}
          </>
        )}

        {holderUsersQuery.isError && (
          <Alert
            type="error"
            showIcon
            message={lang.ORG_STRUCTURE_ERROR}
            className={styles.disclaimer}
          />
        )}
      </Modal>
    </>
  );
}
